import { useState, useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { SUBSCRIPTION_CONFIG } from '../config';

const GET_ACTIVE_SUBSCRIPTION = gql`
  query GetActiveSubscription {
    mySubscriptions {
      id
      status
      expires_at
      starts_at
      package {
        name
        id
      }
    }
  }
`;

export const useSubscriptionCheck = (user) => {
    const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
    
    const { data, loading } = useQuery(GET_ACTIVE_SUBSCRIPTION, {
        skip: !user || user.role !== 'user_admin',
        fetchPolicy: 'cache-and-network'
    });

    // Helper to check if subscription is in grace period (configurable)
    const isInGracePeriod = (expiresAt) => {
        if (!expiresAt) return false;
        
        const now = new Date();
        let expiry;
        
        if (typeof expiresAt === 'string' && !isNaN(expiresAt)) {
            expiry = new Date(parseInt(expiresAt));
        } else if (typeof expiresAt === 'number') {
            expiry = new Date(expiresAt);
        } else {
            expiry = new Date(expiresAt);
        }
        
        if (isNaN(expiry.getTime())) return false;
        
        const diffTime = now - expiry; // Positive if expired
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const gracePeriodDays = SUBSCRIPTION_CONFIG.GRACE_PERIOD_DAYS;
        
        return diffDays > 0 && diffDays <= gracePeriodDays; // Expired but within grace period
    };

    useEffect(() => {
        // Only show popup for user_admin who have verified email but no active subscription
        const subscriptions = data?.mySubscriptions || [];
        const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
        const expiredSubscriptions = subscriptions.filter(sub => sub.status === 'expired');
        
        // Check if any expired subscription is in grace period
        const hasGracePeriodSubscription = expiredSubscriptions.some(sub => 
            isInGracePeriod(sub.expires_at)
        );
        
        const hasActiveSubscription = activeSubscriptions.length > 0;
        
        console.log('🔍 Subscription Check Debug:', {
            user: user?.username,
            role: user?.role,
            emailVerified: user?.email_verified,
            userObject: user,
            loading,
            subscriptions,
            activeSubscriptions,
            expiredSubscriptions,
            hasActiveSubscription,
            hasGracePeriodSubscription
        });
        
        // Don't show popup if:
        // 1. User has active subscription, OR
        // 2. User has expired subscription in grace period (they still have access)
        if (hasActiveSubscription || hasGracePeriodSubscription) {
            console.log('✅ Not showing popup - user has active subscription or in grace period');
            setShowSubscriptionPopup(false);
            return;
        }
        
        // Only show popup for user_admin who have no active subscription and not in grace period
        if (
            user && 
            user.role === 'user_admin' && 
            !loading && 
            !hasActiveSubscription &&
            !hasGracePeriodSubscription
        ) {
            // If user has no active subscription, they need email verification to see subscription options
            if (!user.email_verified) {
                console.log('🚨 User needs email verification before subscribing');
                // Don't show subscription popup if email not verified - they need to verify first
                setShowSubscriptionPopup(false);
                return;
            }
            
            // Check if user has permanently dismissed (only if they have an active subscription)
            const permanentlyDismissed = localStorage.getItem('subscription_permanently_dismissed');
            
            console.log('🚨 Should show popup - no active subscription found', { permanentlyDismissed });
            
            if (!permanentlyDismissed) {
                // Small delay to ensure smooth UX
                const timer = setTimeout(() => {
                    setShowSubscriptionPopup(true);
                }, 1500);
                
                return () => clearTimeout(timer);
            }
        } else {
            console.log('✅ Not showing popup - conditions not met');
            setShowSubscriptionPopup(false);
        }
    }, [user, data, loading]);

    const dismissPopup = () => {
        setShowSubscriptionPopup(false);
        // Don't permanently dismiss - popup will show again on refresh
        // This ensures persistent reminder until user subscribes
    };

    const permanentlyDismiss = () => {
        setShowSubscriptionPopup(false);
        // Only allow permanent dismissal if user explicitly chooses to
        localStorage.setItem('subscription_permanently_dismissed', 'true');
    };

    const onSubscriptionSuccess = (subscription) => {
        setShowSubscriptionPopup(false);
        // Clear any dismissal flags since user now has subscription
        localStorage.removeItem('subscription_permanently_dismissed');
        
        // Show success message
        console.log('✅ Subscription activated:', subscription);
        
        // Optionally trigger a page refresh or update user context
        window.location.reload();
    };

    // Check if user has active subscription or is in grace period
    const subscriptions = data?.mySubscriptions || [];
    const hasActiveSubscription = subscriptions.some(sub => sub.status === 'active');
    const hasGracePeriodAccess = subscriptions.some(sub => 
        sub.status === 'expired' && isInGracePeriod(sub.expires_at)
    );

    return {
        showSubscriptionPopup,
        dismissPopup,
        permanentlyDismiss,
        onSubscriptionSuccess,
        hasActiveSubscription: hasActiveSubscription || hasGracePeriodAccess
    };
};